import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ComposableMap,
  Geographies,
  Geography,
  Sphere
} from 'react-simple-maps';
import styled from 'styled-components';

const MapContainer = styled.div`
  width: 100%;
  height: 500px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  position: relative;
`;

const LegendContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${props => props.color};
`;

const TooltipContainer = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.9rem;
  pointer-events: none;
  z-index: 1000;
  white-space: nowrap;
`;

// URL to world geography data - using local file
const geoUrl = '/countries-110m.json';

// Country name mapping to standardize between data and geography
const COUNTRY_NAME_MAPPING = {
  'United States': 'United States of America',
  'United Kingdom': 'United Kingdom',
  'Czech Republic': 'Czechia', 
  'South Korea': 'South Korea',
  'Russia': 'Russia',
  'Vietnam': 'Vietnam',
  'Iran': 'Iran',
  'Syria': 'Syria',
  'Bolivia': 'Bolivia',
  'Venezuela': 'Venezuela',
  'Tanzania': 'Tanzania',
  'Myanmar': 'Myanmar',
  'North Korea': 'North Korea',
  'Laos': 'Laos',
  'Moldova': 'Moldova',
  'Macedonia': 'North Macedonia',
  // Add more common mappings based on typical data
  'USA': 'United States of America',
  'UK': 'United Kingdom',
  'Germany': 'Germany',
  'France': 'France',
  'Italy': 'Italy',
  'Spain': 'Spain',
  'Canada': 'Canada',
  'Australia': 'Australia',
  'Sweden': 'Sweden',
  'Romania': 'Romania',
  'Denmark': 'Denmark',
  'Latvia': 'Latvia'
};

export const WorldHeatmapView = ({ countryData }) => {
  const [tooltip, setTooltip] = React.useState({ show: false, content: '', x: 0, y: 0 });
  const mapContainerRef = React.useRef(null);

  // Prepare data and calculate color scale
  const { processedData, colorScale, maxValue } = useMemo(() => {
    if (!countryData || !Array.isArray(countryData) || countryData.length === 0) {
      return { processedData: {}, colorScale: [], maxValue: 0 };
    }

    // Create a map of country names to their event counts
    const dataMap = {};
    let max = 0;
    
    countryData.forEach(item => {
      const originalCountry = item.country;
      const mappedCountry = COUNTRY_NAME_MAPPING[originalCountry] || originalCountry;
      
      // Get the count value - it should already be a number from the API
      const eventCount = typeof item.count === 'number' ? item.count : parseInt(item.count) || 0;
      
      dataMap[mappedCountry] = eventCount;
      max = Math.max(max, eventCount);
    });

    // Create color scale based on data distribution
    const colors = [
      '#0f5132', // Darkest green (highest values)
      '#198754', // Dark green
      '#20c997', // Medium green  
      '#6dd58c', // Light green
      '#9dd8a8', // Very light green
      '#d1eddf'  // Lightest green (lowest values)
    ];

    return {
      processedData: dataMap,
      colorScale: colors,
      maxValue: max
    };
  }, [countryData]);

  // Function to get color based on event count
  const getCountryColor = (countryName) => {
    const eventCount = processedData[countryName];
    
    if (!eventCount || eventCount === 0) {
      return '#ced4da'; // More visible gray for no data
    }

    // Calculate color intensity based on value relative to max
    const intensity = eventCount / maxValue;
    
    if (intensity >= 0.8) return colorScale[0]; // Darkest
    if (intensity >= 0.6) return colorScale[1];
    if (intensity >= 0.4) return colorScale[2];
    if (intensity >= 0.2) return colorScale[3];
    if (intensity >= 0.05) return colorScale[4];
    return colorScale[5]; // Lightest
  };

  const handleMouseEnter = (geo, event) => {
    // Get country name from properties
    const countryName = geo.properties?.name || 
                       geo.properties?.NAME || 
                       geo.properties?.NAME_EN;
    
    if (!countryName) return;
    
    const eventCount = processedData[countryName] || 0;
    
    // Get position relative to the map container using ref
    const rect = mapContainerRef.current ? mapContainerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
    
    setTooltip({
      show: true,
      content: `${countryName}: ${eventCount.toLocaleString()} events`,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    });
  };

  const handleMouseMove = (event) => {
    // Get position relative to the map container using ref
    const rect = mapContainerRef.current ? mapContainerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
    
    setTooltip(prev => ({
      ...prev,
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    }));
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  // Create legend items
  const legendItems = useMemo(() => {
    if (maxValue === 0) {
      return [{ color: '#f8f9fa', label: 'No data available' }];
    }
    
    return [
      { color: colorScale[0], label: `${Math.round(maxValue * 0.8).toLocaleString()}+` },
      { color: colorScale[2], label: `${Math.round(maxValue * 0.4).toLocaleString()}+` },
      { color: colorScale[4], label: `${Math.round(maxValue * 0.05).toLocaleString()}+` },
      { color: '#ced4da', label: 'No data' }
    ];
  }, [colorScale, maxValue]);

  return (
    <MapContainer ref={mapContainerRef}>
      <ComposableMap
        projection="geoMercator"
        style={{ width: '100%', height: '100%' }}
        projectionConfig={{
          scale: 150,
          center: [0, 10]
        }}
      >
        <Sphere stroke="none" fill="#ffffff" />
        
        <Geographies geography={geoUrl}>
          {({ geographies, loading, error }) => {
            if (loading) {
              return <text x="400" y="200" fill="#6c757d" textAnchor="middle">Loading map data...</text>;
            }
            
            if (error) {
              console.error('🗺️ Error loading geographic data:', error);
              return <text x="400" y="200" fill="#dc3545" textAnchor="middle">Error loading map data</text>;
            }
            
            return geographies.map((geo) => {
              // Get country name from properties
              const countryName = geo.properties?.name || 
                                 geo.properties?.NAME || 
                                 geo.properties?.NAME_EN;
              
              // Skip if no country name found
              if (!countryName) {
                console.warn('🗺️ Geography item without name, ID:', geo.id, 'Properties:', geo.properties);
                return null;
              }
              
              const fillColor = getCountryColor(countryName);
              
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fillColor}
                  stroke='#343a40'
                  strokeWidth={0.8}
                  style={{
                    default: { 
                      outline: 'none',
                      fill: fillColor,
                      stroke: '#343a40',
                      strokeWidth: 0.8
                    },
                    hover: { 
                      outline: 'none',
                      fill: fillColor === '#ced4da' ? '#adb5bd' : fillColor,
                      filter: 'brightness(0.85)',
                      stroke: '#212529',
                      strokeWidth: 1.2,
                      cursor: 'pointer'
                    },
                    pressed: { 
                      outline: 'none',
                      fill: fillColor,
                      stroke: '#212529',
                      strokeWidth: 1
                    }
                  }}
                  onMouseEnter={(event) => handleMouseEnter(geo, event)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                />
              );
            }).filter(Boolean); // Remove null entries
          }}
        </Geographies>
      </ComposableMap>

      {/* Legend */}
      <LegendContainer>
        <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Event Count:</span>
        {legendItems.map((item, index) => (
          <LegendItem key={index}>
            <LegendColor color={item.color} />
            <span>{item.label}</span>
          </LegendItem>
        ))}
      </LegendContainer>

      {/* Tooltip */}
      {tooltip.show && (
        <TooltipContainer
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 30
          }}
        >
          {tooltip.content}
        </TooltipContainer>
      )}
    </MapContainer>
  );
};

WorldHeatmapView.propTypes = {
  countryData: PropTypes.arrayOf(
    PropTypes.shape({
      country: PropTypes.string.isRequired,
      count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  )
};

WorldHeatmapView.displayName = 'WorldHeatmapView';
